import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import { toast } from "sonner";
import Button from "@/shared/components/Button";
import LinearLayout from "@/shared/components/LinearLayout";
import { isApiError } from "@/shared/api";
import type { ProfileKyc } from "@/features/profile/interfaces/profile.interface";
import { useProfile } from "@/features/profile/services";
import { useRemoteConfigString } from "@/shared/firebase/remoteConfig";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  DOCUMENT_TYPES,
  INCOME_SOURCES,
  OCCUPATIONS,
  OPEN_ACCOUNT_REASONS,
  type IncomeSource,
  type Occupation,
  type RetryKycRequest,
} from "../interfaces/kyc.interface";
import { p100SupportedCountryOptions } from "../constants/supported-countries";
import { retryKycSchema, type RetryKycFormValues } from "../schemas/retry-kyc";
import { useRetryKyc } from "../services";

const formatLabel = (value: string) =>
  value
    .replace(/_/g, " ")
    .replace(/\b([a-z])/gi, (match) => match.toUpperCase());

const documentTypeOptions = DOCUMENT_TYPES.map((value) => ({
  label: formatLabel(value),
  value,
}));

const incomeSourceOptions = INCOME_SOURCES.map((value) => ({
  label: formatLabel(value),
  value,
}));

const accountReasonOptions = OPEN_ACCOUNT_REASONS.map((value) => ({
  label: formatLabel(value),
  value,
}));

const occupationOptions = OCCUPATIONS.map((value) => ({
  label: formatLabel(value),
  value,
}));

const countryOptions = p100SupportedCountryOptions;

const isIncomeSource = (
  value: string | null | undefined
): value is IncomeSource =>
  typeof value === "string" && INCOME_SOURCES.includes(value as IncomeSource);

const isOccupation = (value: string | null | undefined): value is Occupation =>
  typeof value === "string" && OCCUPATIONS.includes(value as Occupation);

const asFieldErrorMessage = (value: unknown) => {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === "string")
      .join(" ");
  }

  return null;
};

const getDefaultRetryFormValues = (
  kyc?: ProfileKyc | null
): RetryKycFormValues => ({
  documentType: "",
  country: "",
  incomeSource: isIncomeSource(kyc?.incomeSource) ? kyc.incomeSource : "",
  openAccountReason: "",
  occupation: isOccupation(kyc?.occupation) ? kyc.occupation : "",
});

const retryFieldNames = new Set<keyof RetryKycFormValues>([
  "documentType",
  "country",
  "incomeSource",
  "openAccountReason",
  "occupation",
]);

const KycFormRejected = () => {
  const kycSupportUrl = useRemoteConfigString("kyc_support");
  const [isRetryModalOpen, setIsRetryModalOpen] = useState(false);
  const { data: profile } = useProfile({
    staleTime: 30_000,
    retry: 1,
  });
  const { mutateAsync: triggerRetryKyc, isPending: isRetrying } = useRetryKyc();

  const form = useForm<RetryKycFormValues>({
    resolver: zodResolver(retryKycSchema),
    defaultValues: getDefaultRetryFormValues(profile?.kyc),
    mode: "onChange",
  });

  const incomeSource = form.watch("incomeSource");
  const showOccupationField = incomeSource === "SALARY";

  useEffect(() => {
    if (!isRetryModalOpen) {
      form.reset(getDefaultRetryFormValues(profile?.kyc));
    }
  }, [form, isRetryModalOpen, profile?.kyc]);

  useEffect(() => {
    if (!showOccupationField && form.getValues("occupation")) {
      form.setValue("occupation", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.clearErrors("occupation");
    }
  }, [form, showOccupationField]);

  const disableSubmit = useMemo(
    () => isRetrying || !form.formState.isValid,
    [form.formState.isValid, isRetrying]
  );

  const handleRetryModalChange = (open: boolean) => {
    if (!open) {
      form.reset(getDefaultRetryFormValues(profile?.kyc));
    }

    setIsRetryModalOpen(open);
  };

  const handleRetrySubmit = form.handleSubmit(async (values) => {
    const parsedValues = retryKycSchema.parse(values);
    const payload = Object.fromEntries(
      Object.entries({
        ...parsedValues,
        occupation:
          parsedValues.incomeSource === "SALARY"
            ? parsedValues.occupation
            : undefined,
      }).filter(([, value]) => value !== undefined)
    ) as RetryKycRequest;

    try {
      const response = await triggerRetryKyc(payload);

      if (!response.redirectUrl) {
        toast.error(
          "Unable to generate a verification link. Please try again shortly."
        );
        return;
      }

      setIsRetryModalOpen(false);
      toast.success("Redirecting to verification...");
      window.location.href = response.redirectUrl;
    } catch (error) {
      if (isApiError(error)) {
        if (error.errors) {
          for (const [field, fieldError] of Object.entries(error.errors)) {
            if (!retryFieldNames.has(field as keyof RetryKycFormValues)) {
              continue;
            }

            form.setError(field as keyof RetryKycFormValues, {
              type: "server",
              message:
                asFieldErrorMessage(fieldError) ?? "Please check this field.",
            });
          }
        }

        toast.error(
          error.message ?? "Unable to retry KYC. Please check your details."
        );
        return;
      }

      toast.error("Unexpected error. Please try again later.");
    }
  });

  const handleContactSupport = () => {
    const targetUrl =
      kycSupportUrl ?? "https://bizbot360.com/ad-leads/kyc_support";
    window.open(targetUrl, "_blank", "noopener");
  };

  return (
    <>
      <div className="w-[350px]">
        <LinearLayout orientation="vertical">
          <div className="mx-auto space-y-6 text-center">
            <h1 className="font-inter w-[272px] mx-auto text-center text-[20px] font-medium leading-[24px] tracking-[0px] text-white opacity-100">
              KYC Verification Failed
            </h1>
            <p className="font-inter mx-auto w-[320px] text-center text-[14px] font-normal leading-[17px] tracking-[0px] text-white opacity-100">
              We were unable to verify your identity. This could be due to
              invalid documents, blurry images, or mismatched information.
            </p>
            <p className="font-inter mx-auto w-[296px] py-10 text-center text-[14px] font-normal leading-[17px] tracking-[0px] text-white opacity-50">
              Please ensure all your details are correct and your documents are
              clearly visible before trying again.
            </p>
            <div className="space-y-3">
              <Button
                variant="primary"
                fluid
                onClick={() => handleRetryModalChange(true)}>
                Retry KYC
              </Button>
              <Button variant="outlined" fluid onClick={handleContactSupport}>
                Contact Support
              </Button>
            </div>
          </div>
        </LinearLayout>
      </div>

      <Dialog open={isRetryModalOpen} onOpenChange={handleRetryModalChange}>
        <DialogContent
          showCloseButton={false}
          className="w-[calc(100%-2rem)] max-w-[360px] gap-0 rounded-[28px] border border-white/10 bg-[#151A1C] p-0 text-white shadow-2xl">
          <DialogHeader className="border-b border-white/8 px-5 py-4 text-left">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1 text-left">
                <DialogTitle className="text-lg font-semibold text-white">
                  Retry KYC
                </DialogTitle>
                <DialogDescription className="text-sm leading-5 text-white/60">
                  Update the details below to start a new verification attempt.
                </DialogDescription>
              </div>
              <button
                type="button"
                onClick={() => handleRetryModalChange(false)}
                className="rounded-full p-2 text-white/60 transition hover:bg-white/10 hover:text-white"
                aria-label="Close retry KYC modal">
                <X className="size-4" />
              </button>
            </div>
          </DialogHeader>

          <div className="px-5 py-5">
            <Form {...form}>
              <form onSubmit={handleRetrySubmit} className="space-y-4">
                <RetrySelectField
                  control={form.control}
                  name="documentType"
                  label="Document Type"
                  placeholder="Select document type"
                  options={documentTypeOptions}
                />
                <RetrySelectField
                  control={form.control}
                  name="country"
                  label="Country of Residence"
                  placeholder="Select country"
                  options={countryOptions}
                />
                <RetrySelectField
                  control={form.control}
                  name="incomeSource"
                  label="Source of Income"
                  placeholder="Select income source"
                  options={incomeSourceOptions}
                />
                <RetrySelectField
                  control={form.control}
                  name="openAccountReason"
                  label="Purpose of Account Opening"
                  placeholder="Select account opening reason"
                  options={accountReasonOptions}
                />

                {showOccupationField && (
                  <RetrySelectField
                    control={form.control}
                    name="occupation"
                    label="Occupation / Profession"
                    placeholder="Select occupation"
                    options={occupationOptions}
                  />
                )}

                <div className="space-y-3 pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    fluid
                    disabled={disableSubmit}
                    loading={isRetrying}>
                    {isRetrying ? "Retrying..." : "Reattempt KYC"}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    fluid
                    onClick={() => handleRetryModalChange(false)}
                    disabled={isRetrying}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

type RetrySelectFieldProps = {
  control: ReturnType<typeof useForm<RetryKycFormValues>>["control"];
  name: keyof RetryKycFormValues;
  label: string;
  placeholder: string;
  options: Array<{ label: string; value: string }>;
};

function RetrySelectField({
  control,
  name,
  label,
  placeholder,
  options,
}: RetrySelectFieldProps) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-xs font-medium uppercase tracking-[0.16em] text-white/65">
            {label}
          </FormLabel>
          <Select value={field.value ?? ""} onValueChange={field.onChange}>
            <FormControl>
              <SelectTrigger className="h-12 w-full rounded-2xl border-white/10 bg-[#0E1214] text-left text-white data-[placeholder]:text-white/35">
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent className="border-white/10 bg-[#171B1D] text-white">
              <SelectGroup>
                <SelectLabel className="text-white/45">{label}</SelectLabel>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export default KycFormRejected;
